import React from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { CellRenderer, DistrictCell, OfficeCell, PartyTag, SeatIdentifierCell } from '../../components';
import {
  CandidateListCandidate,
  ColumnLabel,
  GroupedData,
  LabeledData
} from '../../app/feathers/candidate-lists/CandidateList';
import { District } from '../../app/feathers/districts/District';
import { Office } from '../../app/feathers/offices/Office';
import { Party } from '../../app/feathers/parties/Party';

export type VerifyDataGroup = GroupedData & LabeledData & {
  officeInfo?: Office;
  districtInfo?: District;
  groupedDataIndex: number;
};

export interface VerifyDataRowHandlers {
  toggleExpansion: (groupedDataIndex: number) => void;
  toggleSelection: (groupedDataIndex: number, selected: boolean) => void;
  changeOffice: (groupLabel: string, office: Office, confidence: number) => void;
  changeDistrict: (groupLabel: string, district: District, confidence: number) => void;
  changeSpecialElection: (groupLabel: string, isSpecial: boolean) => void;
  changeSeatIdentifier: (
    groupLabel: string,
    seatIdentifier: string | null,
    seatIdentifierType: string | null
  ) => void;
  deleteGroup: (groupedDataIndex: number) => void;
}

interface VerifyDataRowProps {
  group: VerifyDataGroup;
  rowIndex: number;
  isExpanded: boolean;
  isSelected: boolean;
  selectionDisabled: boolean;
  duplicateReason?: string;
  electionState: string;
  electionIsPartisan: boolean;
  allowedPartyOptions: Party[];
  officeDataLoading: boolean;
  districtDataLoading: boolean;
  handlersRef: React.MutableRefObject<VerifyDataRowHandlers>;
}

const INFO_FIELDS = ['Email', 'Phone', 'Website', 'Address'];

const VerifyDataRow: React.FC<VerifyDataRowProps> = ({
  group,
  rowIndex,
  isExpanded,
  isSelected,
  selectionDisabled,
  duplicateReason,
  electionState,
  electionIsPartisan,
  allowedPartyOptions,
  officeDataLoading,
  districtDataLoading,
  handlersRef
}) => (
  <>
    <tr style={{ backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#fafafa' }}>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
        <Checkbox
          checked={isSelected}
          disabled={selectionDisabled}
          inputProps={{ 'aria-label': `select ${group.groupLabel}` }}
          onChange={event => (
            handlersRef.current.toggleSelection(group.groupedDataIndex, event.target.checked)
          )}
          size="small"
          sx={{ padding: '4px' }}
        />
        <Button
          onClick={() => handlersRef.current.toggleExpansion(group.groupedDataIndex)}
          style={{ minWidth: 'auto', padding: '4px', color: '#666' }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px', fontWeight: '500' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{group.groupLabel}</span>
          {duplicateReason && (
            <Tooltip
              title={
                duplicateReason === 'conflicting-seat-identifiers'
                  ? 'This row needs a seat identifier to distinguish it from similar races'
                  : duplicateReason === 'duplicate-rows'
                    ? 'This row has the same office, district, and party as another row'
                    : 'This row needs attention'
              }
              arrow
              placement="top"
            >
              <WarningIcon style={{ color: '#f57c00', fontSize: '18px', cursor: 'help' }} />
            </Tooltip>
          )}
        </div>
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
        <OfficeCell
          office={group.officeInfo}
          suggestCreationAs={group.suggestsOfficeCreationAs}
          onOfficeChange={office => handlersRef.current.changeOffice(group.groupLabel, office, 1)}
          district={null}
          districtId={electionState}
          loading={officeDataLoading}
          confidence={group.officeConfidence || 0}
          onChangeConfidence={confidence => {
            if (group.officeInfo) {
              handlersRef.current.changeOffice(group.groupLabel, group.officeInfo, confidence);
            }
          }}
        />
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
        <DistrictCell
          disableEditing={!group.officeInfo || (group.officeConfidence || 0) < 0.5}
          limitTypes={group.officeInfo?.districtTypes}
          district={group.districtInfo}
          suggestCreationAs={group.suggestsDistrictCreationAs}
          onDistrictChange={district => handlersRef.current.changeDistrict(group.groupLabel, district, 1)}
          state={electionState}
          loading={districtDataLoading}
          confidence={group.districtConfidence || 0}
          onChangeConfidence={confidence => {
            if (group.districtInfo) {
              handlersRef.current.changeDistrict(group.groupLabel, group.districtInfo, confidence);
            }
          }}
        />
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={group.position?.special || false}
              onChange={event => handlersRef.current.changeSpecialElection(group.groupLabel, event.target.checked)}
              color="primary"
              size="small"
            />
          }
          label=""
          style={{ margin: 0 }}
        />
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
        <SeatIdentifierCell
          seatIdentifier={group.position?.seatIdentifier}
          seatIdentifierType={group.position?.seatIdentifierType}
          onSeatIdentifierChange={(seatIdentifier, seatIdentifierType) => (
            handlersRef.current.changeSeatIdentifier(group.groupLabel, seatIdentifier, seatIdentifierType)
          )}
        />
      </td>
      {electionIsPartisan && (
        <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
          <PartyTag party={group.party || ''} preloadedParties={allowedPartyOptions} />
        </td>
      )}
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
        {group.maxChoices}
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>
        {group.candidates.length}
      </td>
      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
        <Tooltip title="Delete row" arrow placement="top">
          <IconButton
            size="small"
            disabled={selectionDisabled}
            onClick={() => handlersRef.current.deleteGroup(group.groupedDataIndex)}
            sx={{ color: '#666' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </td>
    </tr>
    {isExpanded && (
      <tr>
        <td
          colSpan={electionIsPartisan ? 10 : 9}
          style={{ padding: 0, borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}
        >
          <div style={{ padding: '2px 0 2px 70px' }}>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f5f5f5' }}>
                  <tr>
                    {['Name', 'Party', 'Incumbent', 'Write-in', ...INFO_FIELDS].map(field => (
                      <th
                        key={field}
                        style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bold' }}
                      >
                        {field.charAt(0).toUpperCase() + field.slice(1)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.candidates.map((candidate: CandidateListCandidate, candidateIndex: number) => (
                    <tr key={candidateIndex} style={{ backgroundColor: candidateIndex % 2 === 0 ? '#fff' : '#fafafa' }}>
                      {['name', 'party', 'incumbent', 'writeIn', ...INFO_FIELDS].map(field => {
                        const fieldAsLabel = field.toLowerCase() as ColumnLabel;
                        const candidateRecord = candidate as unknown as Record<string, any>;
                        const candidateInfo = candidate.info as unknown as Record<string, any>;
                        const value = candidateRecord[fieldAsLabel] ?? candidateInfo[fieldAsLabel];
                        return (
                          <td key={field} style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                            <CellRenderer columnLabel={fieldAsLabel} value={value} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    )}
  </>
);

export default React.memo(VerifyDataRow);
